import { COHORT_GRACE_MS } from './publicNotice';

/**
 * The pending join a signed-out visitor carries through registration and
 * email verification (docs/plans/CIRCLE-STARTING-NOTICE.md §5, N12, S4).
 *
 * `/register?circle=<id>` or `/register?hourly=<T>`: read only when they
 * validate, stored as NUMBERS in localStorage['ee.pendingCircleJoin'], and
 * never used as a URL or a redirect (the app builds every path itself).
 * PendingCircleJoin (behind VerifyEmailGate) takes it once and clears it.
 */
export const PENDING_CIRCLE_JOIN_KEY = 'ee.pendingCircleJoin';

/** A stored join older than this is dropped unread (a browser someone else signs into later). */
export const PENDING_MAX_AGE_MS = 24 * 3_600_000;

export type PendingCircleJoin =
  | {
      kind: 'hourly';
      hourlyAt: number;
      /** T, ISO. */
      startsAt: string;
      /** T: the hourly circle closes at its start (N14). */
      until: string;
      savedAt: number;
    }
  | {
      kind: 'cohort';
      cohortId: number;
      /** The session's T when the notice knew it (the host saves it on Join); null from a bare link. */
      startsAt: string | null;
      /** T+5 (walk-ins still work in the opening phases), or null. */
      until: string | null;
      savedAt: number;
    };

const CIRCLE_RE = /^\d{1,10}$/;
const HOURLY_RE = /^\d{10}$/;

function isoZ(ms: number): string {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * S4: `circle` matches ^\d{1,10}$ (and is not 0); `hourly` matches ^\d{10}$,
 * is a whole hour and lies within [now - 5 min, now + 24 h]. Anything else is
 * ignored, never echoed.
 */
export function parsePendingJoinParams(
  params: URLSearchParams,
  nowMs: number = Date.now(),
): PendingCircleJoin | null {
  const circle = params.get('circle');
  if (circle !== null && CIRCLE_RE.test(circle) && Number(circle) > 0) {
    return {
      kind: 'cohort',
      cohortId: Number(circle),
      startsAt: null,
      until: null,
      savedAt: nowMs,
    };
  }
  const hourly = params.get('hourly');
  if (hourly !== null && HOURLY_RE.test(hourly)) {
    const t = Number(hourly);
    const ms = t * 1000;
    if (t % 3600 === 0 && ms >= nowMs - 5 * 60_000 && ms <= nowMs + 24 * 3_600_000) {
      return { kind: 'hourly', hourlyAt: t, startsAt: isoZ(ms), until: isoZ(ms), savedAt: nowMs };
    }
  }
  return null;
}

/** The pending join for a cohort session the notice showed (it knows T). */
export function cohortPendingJoin(
  cohortId: number,
  startsAt: string,
  nowMs = Date.now(),
): PendingCircleJoin {
  const t = Date.parse(startsAt);
  return {
    kind: 'cohort',
    cohortId,
    startsAt: isoZ(t),
    until: isoZ(t + COHORT_GRACE_MS),
    savedAt: nowMs,
  };
}

function sameTarget(a: PendingCircleJoin, b: PendingCircleJoin): boolean {
  if (a.kind === 'hourly' && b.kind === 'hourly') return a.hourlyAt === b.hourlyAt;
  if (a.kind === 'cohort' && b.kind === 'cohort') return a.cohortId === b.cohortId;
  return false;
}

/** Store it, keeping a richer stored copy of the same target (the host's, which knows T). */
export function savePendingJoin(join: PendingCircleJoin): void {
  try {
    const existing = readPendingJoin();
    if (existing && sameTarget(existing, join) && existing.startsAt && !join.startsAt) return;
    window.localStorage.setItem(PENDING_CIRCLE_JOIN_KEY, JSON.stringify(join));
  } catch {
    /* no storage: the visitor simply is not auto-joined */
  }
}

function num(v: unknown, re: RegExp): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 && re.test(String(v)) ? v : null;
}

function isoOrNull(v: unknown): string | null {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(v) ? v : null;
}

/** Read and validate (storage is user-writable: re-check every field). */
export function readPendingJoin(nowMs: number = Date.now()): PendingCircleJoin | null {
  try {
    const raw = window.localStorage.getItem(PENDING_CIRCLE_JOIN_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    const savedAt = typeof p.savedAt === 'number' ? p.savedAt : NaN;
    if (!Number.isFinite(savedAt) || nowMs - savedAt > PENDING_MAX_AGE_MS) return null;
    if (p.kind === 'hourly') {
      const t = num(p.hourlyAt, HOURLY_RE);
      if (t === null || t % 3600 !== 0) return null;
      const iso = isoZ(t * 1000);
      return { kind: 'hourly', hourlyAt: t, startsAt: iso, until: iso, savedAt };
    }
    if (p.kind === 'cohort') {
      const id = num(p.cohortId, CIRCLE_RE);
      if (id === null) return null;
      const startsAt = isoOrNull(p.startsAt);
      return {
        kind: 'cohort',
        cohortId: id,
        startsAt,
        until: startsAt ? isoZ(Date.parse(startsAt) + COHORT_GRACE_MS) : null,
        savedAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** One-shot: read it and clear it, so a later sign-in never replays it. */
export function takePendingJoin(nowMs: number = Date.now()): PendingCircleJoin | null {
  const join = readPendingJoin(nowMs);
  try {
    window.localStorage.removeItem(PENDING_CIRCLE_JOIN_KEY);
  } catch {
    /* nothing to clear */
  }
  return join;
}
